"use client";

import { Loader2, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  loading?: boolean;
  clearLabel?: string;
  className?: string;
  size?: "default" | "sm";
  autoFocus?: boolean;
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  loading,
  clearLabel,
  className,
  size = "default",
  autoFocus,
}: SearchInputProps) {
  const isSmall = size === "sm";

  return (
    <label
      className={cn(
        "flex items-center rounded-lg border border-input-border bg-input-bg px-3 cursor-text",
        "transition-colors hover:bg-secondary-dark focus-within:bg-input-bg-focus focus-within:border-input-border-focus focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-0",
        isSmall ? "gap-2 py-2" : "h-12 gap-2.5",
        className
      )}
    >
      {loading ? (
        <Loader2
          className={cn(
            "shrink-0 animate-spin text-muted-foreground",
            isSmall ? "size-3.5" : "size-4"
          )}
          aria-hidden
        />
      ) : (
        <Search
          className={cn("shrink-0 text-muted-foreground", isSmall ? "size-3.5" : "size-4")}
          aria-hidden
        />
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={cn(
          "flex-1 bg-transparent outline-none placeholder:text-muted-foreground/50",
          isSmall ? "text-sm" : "h-full text-base"
        )}
        aria-label={placeholder}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={clearLabel}
          className={cn(
            "shrink-0 items-center justify-center rounded-full bg-muted-foreground/30 text-background hover:bg-muted-foreground/50 cursor-pointer",
            isSmall ? "flex size-4" : "flex size-5"
          )}
        >
          <X className={isSmall ? "size-3" : "size-3.5"} />
        </button>
      )}
    </label>
  );
}
